import { useCallback, useState } from 'react';

import { submitPaymentLinkV2 } from '../../../../services/backendPaymentsV2Services';

import { captureException } from '../helpers/captureException';
import { buildPaymentPayload } from '../helpers/paymentPayload';
import { resolveCustomerAccount } from '../helpers/resolveCustomerAccount';

import {
  IPaymentLinkFormValues,
  ISubmitPaymentLinkOneTimePayment,
} from '../types/types';

const MISSING_PLAN_MESSAGE =
  'Não foi possível identificar o plano deste pagamento.';

const CUSTOMER_CONFLICT_MESSAGE =
  'E-mail e CPF/CNPJ não correspondem à mesma conta. Se você já tem conta na Jusfy, use os mesmos dados dela.';

interface IOneTimePaymentResult {
  method: 'pix' | 'boleto';
  data: ISubmitPaymentLinkOneTimePayment;
}

interface UsePaymentLinkSubmitParams {
  link: string;
  planId: string | undefined;
  reportError: (message: string | null) => void;
}

export const usePaymentLinkSubmit = ({
  link,
  planId,
  reportError,
}: UsePaymentLinkSubmitParams) => {
  const [isLoading, setIsLoading] = useState(false);
  const [paymentSubmitted, setPaymentSubmitted] = useState(false);
  const [paymentFailed, setPaymentFailed] = useState(false);
  const [oneTimePaymentResult, setOneTimePaymentResult] =
    useState<IOneTimePaymentResult | null>(null);
  const [firstAccessEmail, setFirstAccessEmail] = useState<string | null>(null);

  const submitPayment = useCallback(
    async (values: IPaymentLinkFormValues) => {
      if (!planId) {
        reportError(MISSING_PLAN_MESSAGE);
        return;
      }

      setIsLoading(true);
      reportError(null);

      try {
        const accountStatus = values.requiresCustomerData
          ? await resolveCustomerAccount(values.email, values.document)
          : 'existing';

        if (accountStatus === 'conflict') {
          reportError(CUSTOMER_CONFLICT_MESSAGE);
          return;
        }

        setFirstAccessEmail(
          accountStatus === 'new' ? values.email.trim() : null,
        );

        const payload = await buildPaymentPayload(values, String(planId));

        const { data } = await submitPaymentLinkV2(link, payload);

        const isOneTimeMethod = values.paymentMethodSelected !== 'credit_card';
        const oneTimePayment = data.oneTimePayment;

        if (isOneTimeMethod && oneTimePayment) {
          setOneTimePaymentResult({
            method: values.paymentMethodSelected as 'pix' | 'boleto',
            data: oneTimePayment,
          });
          return;
        }

        if (data.status === 'ACTIVE') {
          setPaymentSubmitted(true);
          return;
        }

        setPaymentFailed(true);
      } catch (error) {
        captureException('payment_submit', error);
        setPaymentFailed(true);
      } finally {
        setIsLoading(false);
      }
    },
    [link, planId, reportError],
  );

  const dismissFailure = useCallback(() => setPaymentFailed(false), []);

  const closeOneTimePayment = useCallback(
    () => setOneTimePaymentResult(null),
    [],
  );

  return {
    isLoading,
    paymentSubmitted,
    paymentFailed,
    oneTimePaymentResult,
    submitPayment,
    dismissFailure,
    closeOneTimePayment,
    firstAccessEmail,
  };
};

export default usePaymentLinkSubmit;
